/**
 * The game's own analytics: a small tally of what happens while you play,
 * sent every couple of minutes and when you put the game away. No cookies and
 * nothing about who you are: the server adds each tally into the day's totals,
 * and notes "played today" under a scrambled id that changes every day
 * (worker/stats.ts). The totals are read on a private page (stats.html).
 */
import type { BetaReport } from './beta';

/**
 * Moments counted into the day's totals. Names are letters only (the server
 * stamps them into its SQL and drops anything else, worker/stats.ts), so
 * "turn 1" is spelled TurnOne. New ones go at the end.
 */
export const STAT_KEYS = [
  // Laps finished (clean or not), clean ones, personal bests.
  'laps',
  'validLaps',
  'bests',
  // Links shared (copy or send), and a friend's challenge step by step: opened, watched, raced, beaten, sent back.
  'shares',
  'linkOpened',
  'linkWatched',
  'linkRaced',
  'linkBeat',
  'sentBack',
  // An old link (its race over), and one that led nowhere.
  'oldLinkOpened',
  'linkGone',
  // The leaderboard opened, a lap watched from it, yesterday's winner watched.
  'boardViews',
  'replays',
  'winnerWatched',
  // The poll: enjoying it? And after a no, what's missing.
  'pollUp',
  'pollDown',
  'pollSkip',
  'whyHard',
  'whyControls',
  'whySlow',
  'whySamey',
  // The driving lesson, step by step: offered before a first race, taken or skipped, left midway, turn 1 mastered,
  // the practice lap finished. Tries: the attempts at turn 1 it took, added up (so tries / mastered is the average).
  // The instructor's demo shown, and the hidden brake assist switched on.
  'lessonOffered',
  'lessonTaken',
  'lessonSkipped',
  'lessonLeft',
  'lessonTurnOne',
  'lessonDone',
  'lessonTries',
  'lessonDemo',
  'lessonAssist',
  // Normal races started (restarts included, the lesson not), and those that got past turn 1.
  'raceStarts',
  'pastTurnOne',
  // Where challenges were sent from: the poster's ticket (start or pause), the leaderboard (or its card).
  'sharesPoster',
  'sharesBoard',
  // How sharp the 3D view settled on each visit's device (2, 1.75, 1.5, 1.25 pixels a point), and devices
  // that drop frames even at the lowest.
  'resFull',
  'resHigh',
  'resMid',
  'resLow',
  'resSlow',
  // Players who arrived from the game's old address with their name and laps (game/move.ts).
  'movedIn',
  // A first clean lap put on the board under a name the game made up ("Pilota 48"), and those renamed after.
  'madeUpName',
  'madeUpRenamed',
  // Battles: the news shown ("Marco beat you"), taken on ("take it back"), and won (their lap beaten back).
  'battleShown',
  'battleTaken',
  'battleWon',
  // The share nudge ("can they catch you?"): shown, and its link sent from it.
  'nudgeShown',
  'nudgeShared',
  // Sharpness below 1 (a touch softer, for a device that drops frames at 1), a computer on the light scene
  // (it dropped frames even at the lowest), and a browser drawing without the graphics card.
  'resSoft',
  'resLight',
  'gpuSoftware',
  // Your own best beside a lap watched: replays it ran in, hidden with its chip, and followed by the camera.
  'ownGhost',
  'ownGhostHidden',
  'ownGhostFollowed',
  // The morning paper: shown (the day after a race), and its clipping shared.
  'paperShown',
  'paperShared',
  // Read again from yesterday's podium.
  'paperReread',
  // Its line to il paddock (the Grand Premio server on Discord) tapped (from 20 Oct 2026).
  'paddockJoin',
  // Played inside Discord (signed in through it), and those whose Discord sign-in failed (played as a guest).
  'discordVisits',
  'discordGuest',
  // Phones (from 10 Oct 2026): the pedal slider switched on and back off on the pause poster, races driven with
  // it, and its how-to shown.
  'sliderOn',
  'sliderOff',
  'sliderRaces',
  'sliderDemo',
  // Stuck or crashed, every device (game/rescue.ts): how to back out shown, and backed out while it was up; the
  // restart suggested, and pressed while it was up; and players who did either on their own (then never shown).
  'backOutHint',
  'backedOut',
  'restartHint',
  'restartAfterHint',
  'knewBackOut',
  'knewRestart',
  // The pause poster's screen shake switch (from 10 Oct 2026): switched off, and back on.
  'shakeOff',
  'shakeOn',
  // Inside a Discord server: started watching someone drive, live (src/game/liveWatch.ts); and races with friends'
  // cars on the track at the same time (once a run).
  'liveWatch',
  'liveTogether',
  // On the website, the way to Discord: the "race together" card shown (once a browser) and put off ("not now"), the
  // line over race tapped, and the add buttons (to a server, to your own apps).
  'discordCard',
  'discordCardLater',
  'discordDoor',
  'discordAddServer',
  'discordAddApps',
  // The "on track now" strip's "show them on my track" (inside a Discord server): switched off, and back on.
  'friendsOff',
  'friendsOn',
  // A friend's lap being raced (a link, Discord's "Race their ghost", a battle): its ghost dropped with ✕.
  'ghostDropped',
  // On the website: signed in with Discord, and signed out.
  'discordSignIn',
  'discordSignOut',
  // Inside Discord: the invite window opened from under race.
  'discordInvite',
  // The channel's "on track now" (from 12 Oct 2026): the game opened from its "Watch" and its "Race with"; the
  // leaderboard's "show me here while I drive" switched off, and back on.
  'liveLaunchWatch',
  'liveLaunchRace',
  'liveShownOff',
  'liveShownOn',
  // "Bring the daily race to <a server you run>" (from 12 Oct 2026): the card shown, its add button, "not now".
  'bringShown',
  'bringAdd',
  'bringLater',
  // Il paddock joined from the "race together" card on the website (from 20 Oct 2026); the paper's line is paddockJoin.
  'paddockJoinCard',
] as const;
export type StatKey = (typeof STAT_KEYS)[number];

/**
 * What the server adds up each day: the moments, plus visits and time, pages opened and left untouched by a
 * browser new to the game (most often automated scanners, which look like any browser): not players; and the
 * laps that would have led their board, replayed by the server: true to their time, or taken off it (made up).
 */
export type TotalKey = StatKey | 'visits' | 'racingMs' | 'watchingMs' | 'opened' | 'lapsReplayed' | 'fakeLaps';

/**
 * How far a player got that day, in order (net/statsReporter.ts keeps the furthest): touched the page, pressed
 * race, shown the lesson's offer, past it (took the lesson, skipped it, or had no offer), pressed the gas, the
 * car moving, a lap finished. Kept per player as the furthest one's place in this list: where the new players who
 * never drive stop. New ones go at the end.
 */
export const FIRST_STEPS = ['touched', 'race', 'offer', 'answered', 'gas', 'moving', 'lap'] as const;
export type FirstStep = (typeof FIRST_STEPS)[number];

export type Device = 'phone' | 'computer';
export type InputKind = 'touch' | 'keys' | 'pad';

/**
 * A new player's first day, summed up by their own device (from 12 Oct 2026): races started (restarts too), how
 * far round the lap they got at best (in tenths; 10: a lap finished), and where most of their unfinished races
 * ended (a tenth, or -1: none did). Sent through that first day; then once more, the first time the device comes
 * back on a later day. The server counts both in the same buckets, so who comes back can be set beside how their
 * first day went, with nobody followed from one day to the next.
 */
export interface FirstVisit {
  tries: number;
  far: number;
  stuck: number;
}

/** A first day, sent again by a device back on a later day: that first day's date and device too. */
export interface CameBack extends FirstVisit {
  first: string;
  device: Device;
}

/** Races started on a first day, in bands (their lower ends): 1, 2–3, 4–9, 10–29, 30 or more. */
export const TRIES_BANDS = [1, 2, 4, 10, 30] as const;

export function triesBand(tries: number): number {
  let b = 0;
  TRIES_BANDS.forEach((lo, i) => {
    if (tries >= lo) b = i;
  });
  return b;
}

/** A first day's bucket: "<tries band>|<far>|<stuck>". */
export function firstVisitKey(v: FirstVisit): string {
  return `${triesBand(v.tries)}|${v.far}|${v.stuck}`;
}

/** A bucket's parts again (null: not one). */
export function firstVisitParts(key: string): { band: number; far: number; stuck: number } | null {
  const m = /^([0-4])\|(10|[0-9])\|(-1|[0-9])$/.exec(key);
  return m ? { band: Number(m[1]), far: Number(m[2]), stuck: Number(m[3]) } : null;
}

/**
 * Cohorts (from 13 Oct 2026): a new player's groups, fixed by their first day: how far round the lap they got
 * (farBand), how they arrived ('l' a friend's link, 'd' inside Discord, 'w' straight to the website), whether
 * they took the lesson, and whether a lap of theirs went on the leaderboard. "<far band>|<src>|<lesson>|<board>".
 */
export type CohortSource = 'w' | 'l' | 'd';

/** How far a first day got: under 20% round, 20–49%, halfway or more, a lap finished. */
export function farBand(far: number): number {
  return far >= 10 ? 3 : far >= 5 ? 2 : far >= 2 ? 1 : 0;
}

export function cohortSeg(o: { far: number; src: CohortSource; lesson: boolean; board: boolean }): string {
  return `${farBand(o.far)}|${o.src}|${o.lesson ? 1 : 0}|${o.board ? 1 : 0}`;
}

export function cohortSegParts(seg: string): { far: number; src: CohortSource; lesson: boolean; board: boolean } | null {
  const m = /^([0-3])\|([wld])\|([01])\|([01])$/.exec(seg);
  return m ? { far: Number(m[1]), src: m[2] as CohortSource, lesson: m[3] === '1', board: m[4] === '1' } : null;
}

/** A device back on day `day` after its first (1–30), with its groups; `stayed`: this is its third day, within two weeks. */
export interface CohortPing {
  first: string;
  day: number;
  device: Device;
  seg: string;
  stayed?: boolean;
}

/** Days after the first that a device still counts in, and the span "stayed" is judged over (its first 14 days). */
export const COHORT_DAYS = 30;
export const STAY_DAYS = 3;
export const STAY_WITHIN = 13;

/**
 * A cohort (the new players of one first day), by device and group: how many (`size`; only those who started a
 * race), how many played on each later day (`active[day]`), and how many stayed (3+ days in their first two weeks).
 */
export interface CohortDay {
  size: Partial<Record<Device, Record<string, number>>>;
  active: Partial<Record<Device, Record<string, Record<string, number>>>>;
  stayed: Partial<Record<Device, Record<string, number>>>;
}

const isInt = (v: unknown, lo: number, hi: number): v is number => typeof v === 'number' && Number.isInteger(v) && v >= lo && v <= hi;

function firstVisitOk(v: unknown): v is FirstVisit {
  const f = v as Partial<FirstVisit> | null;
  return !!f && typeof f === 'object' && isInt(f.tries, 1, 9999) && isInt(f.far, 0, 10) && isInt(f.stuck, -1, 9);
}

/** One tally, as sent. */
export interface StatsBody {
  playerId: string;
  device: Device;
  input: InputKind;
  /** The first tally of a visit (a page load). */
  visit: boolean;
  /** This browser's first day playing. */
  isNew: boolean;
  /** The last day this browser played before today ('' if never, or not known). */
  prevDay: string;
  /** Time actually driving (the car moving) since the last tally, ms. */
  racingMs: number;
  /** Time watching someone's lap since the last tally, ms. */
  watchingMs: number;
  /** A lap went on the leaderboard since the last tally. */
  saved: boolean;
  /** The player has a name for the board (games from before this was sent leave it out). */
  named?: boolean;
  counts: Partial<Record<StatKey, number>>;
  /**
   * A browser new to the game opened the page and is leaving it untouched (no key, tap or controller): counted
   * as that alone, not as a player (net/statsReporter.ts).
   */
  opened?: boolean;
  /** This visit was counted as opened and left, but was touched after all: take that back. */
  reopened?: boolean;
  /** A friend's link brought the player today (working, over or gone; never their own). Older games leave it out. */
  viaLink?: boolean;
  /** The player's first day, if a friend's link brought them that day ('' if not). */
  recruit?: string;
  /** The player's first day was yesterday: a new player back the next day. */
  dayTwo?: boolean;
  /** A friend's link opened for the first time on this browser (its id): one more for that link's reach. */
  link?: string;
  /** The player is in a battle today (raced a friend's link, or sent theirs): the answer brings the news. */
  news?: boolean;
  /** The furthest of FIRST_STEPS this visit has reached (its place in the list). Older games leave it out. */
  reached?: number;
  /** The lesson: taken (true) or its offer skipped (false); left out when neither happened. */
  tookLesson?: boolean;
  /** On a player's first day: how it's going so far (FirstVisit). */
  firstVisit?: FirstVisit;
  /** Once, the first time a device is back on a later day: how its first day went. */
  cameBack?: CameBack;
  /** Playing inside Discord (games from before this was sent leave it out). */
  discord?: boolean;
  /** Once a day on each later day a new player's device plays (from 13 Oct 2026): back on day N, and its groups. */
  cohort?: CohortPing;
  /** On a player's first day (from 13 Oct 2026): where they came from (net/arrival.ts, a SITE label). */
  site?: string;
  /** Once, the first time a device is back on a later day: where its first day came from. */
  siteBack?: { first: string; site: string };
  /** The device clock's offset from UTC, minutes (120: Rome in summer). From 14 Oct 2026; older games leave it out. */
  utcOffset?: number;
}

/** A tally covers a few minutes; anything beyond this is not a real one. */
export const TIME_MAX = 30 * 60_000;
const COUNT_MAX = 1000;
const DATE = /^\d{4}-\d{2}-\d{2}$/;
const PLAYER_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
/** A shared lap's link id (as net/protocol.ts's SHARE_ID). */
const LINK_ID = /^[A-Za-z0-9]{10}$/;
/** Where a new player came from, as net/arrival.ts labels it ("reddit", "from:simracing", "direct"…). */
export const SITE = /^[a-z0-9][a-z0-9:-]{0,39}$/;

/** Why a tally can't be counted, or null. */
export function checkStats(b: unknown): string | null {
  if (!b || typeof b !== 'object') return 'bad request';
  const s = b as Partial<StatsBody>;
  if (typeof s.playerId !== 'string' || !PLAYER_ID.test(s.playerId)) return 'bad player id';
  if (s.device !== 'phone' && s.device !== 'computer') return 'bad device';
  if (s.input !== 'touch' && s.input !== 'keys' && s.input !== 'pad') return 'bad input';
  if (typeof s.visit !== 'boolean' || typeof s.isNew !== 'boolean' || typeof s.saved !== 'boolean') return 'bad flags';
  if (typeof s.prevDay !== 'string' || (s.prevDay !== '' && !DATE.test(s.prevDay))) return 'bad day';
  for (const t of [s.racingMs, s.watchingMs]) if (!Number.isInteger(t) || (t as number) < 0 || (t as number) > TIME_MAX) return 'bad time';
  if (!s.counts || typeof s.counts !== 'object' || Array.isArray(s.counts)) return 'bad counts';
  if ((s.opened !== undefined && typeof s.opened !== 'boolean') || (s.reopened !== undefined && typeof s.reopened !== 'boolean')) return 'bad flags';
  if (s.named !== undefined && typeof s.named !== 'boolean') return 'bad flags';
  if ((s.viaLink !== undefined && typeof s.viaLink !== 'boolean') || (s.dayTwo !== undefined && typeof s.dayTwo !== 'boolean')) return 'bad flags';
  if (s.news !== undefined && typeof s.news !== 'boolean') return 'bad flags';
  if (s.tookLesson !== undefined && typeof s.tookLesson !== 'boolean') return 'bad flags';
  if (s.reached !== undefined && (!Number.isInteger(s.reached) || s.reached < 0 || s.reached >= FIRST_STEPS.length)) return 'bad step';
  if (s.recruit !== undefined && (typeof s.recruit !== 'string' || (s.recruit !== '' && !DATE.test(s.recruit)))) return 'bad day';
  if (s.link !== undefined && (typeof s.link !== 'string' || !LINK_ID.test(s.link))) return 'bad link';
  if (s.firstVisit !== undefined && !firstVisitOk(s.firstVisit)) return 'bad first day';
  if (s.discord !== undefined && typeof s.discord !== 'boolean') return 'bad flags';
  if (s.cohort !== undefined) {
    const c = s.cohort as Partial<CohortPing>;
    if (
      !c ||
      typeof c !== 'object' ||
      typeof c.first !== 'string' ||
      !DATE.test(c.first) ||
      !isInt(c.day, 1, COHORT_DAYS) ||
      (c.device !== 'phone' && c.device !== 'computer') ||
      typeof c.seg !== 'string' ||
      !cohortSegParts(c.seg) ||
      (c.stayed !== undefined && typeof c.stayed !== 'boolean')
    )
      return 'bad cohort';
  }
  if (s.site !== undefined && (typeof s.site !== 'string' || !SITE.test(s.site))) return 'bad site';
  if (s.siteBack !== undefined) {
    const b = s.siteBack as Partial<{ first: string; site: string }> | null;
    if (!b || typeof b !== 'object' || typeof b.first !== 'string' || !DATE.test(b.first) || typeof b.site !== 'string' || !SITE.test(b.site)) return 'bad site';
  }
  if (s.utcOffset !== undefined && (!Number.isInteger(s.utcOffset) || Math.abs(s.utcOffset) > 14 * 60)) return 'bad clock';
  if (s.cameBack !== undefined && (!firstVisitOk(s.cameBack) || typeof s.cameBack.first !== 'string' || !DATE.test(s.cameBack.first) || (s.cameBack.device !== 'phone' && s.cameBack.device !== 'computer')))
    return 'bad first day';
  for (const [k, v] of Object.entries(s.counts)) {
    if (!(STAT_KEYS as readonly string[]).includes(k) || !Number.isInteger(v) || (v as number) < 0 || (v as number) > COUNT_MAX) return 'bad counts';
  }
  return null;
}

/**
 * Who played on a day, added up from the day's players (live for today and
 * yesterday, folded into the day's totals every night after that).
 */
export interface PlayerTotals {
  players: number;
  /** Playing for the first time. */
  newPlayers: number;
  /** Played the day before too (so the day before's players "came back next day"). */
  fromYesterday: number;
  /** Finished at least one lap. */
  finished: number;
  /** Put a lap on the leaderboard. */
  saved: number;
  /**
   * Players whose clean laps and name were recorded (from 6 Oct 2026; left out on days before): the three
   * below are counted among them.
   */
  measured?: number;
  /** Set at least one clean lap. */
  clean?: number;
  /** Set a clean lap, but nothing went on the board. */
  cleanNotSaved?: number;
  /** Set a clean lap, and never had a name for the board all day. */
  cleanNoName?: number;
  /**
   * Players whose friends' links were recorded (from 7 Oct 2026; left out on days before): the counts below are
   * among them.
   */
  linkMeasured?: number;
  /** New players among them. */
  linkMeasuredNew?: number;
  /** Opened a friend's link (working, over or gone; never their own). */
  byLink?: number;
  /** New players a friend's link brought, and of them, those who finished a lap and put one on the board. */
  byLinkNew?: number;
  byLinkNewFinished?: number;
  byLinkNewSaved?: number;
  /** New the day before, back today: all of them, and those a friend's link brought (of the day before's byLinkNew). */
  secondDay?: number;
  recruitsNextDay?: number;
  /**
   * New players' first steps (recorded from 7 Oct 2026), by device: how many got at least as far as each of
   * FIRST_STEPS, and of those who never got the car moving, how many took the lesson, skipped it, or neither.
   */
  firstSteps?: Partial<Record<Device, FirstSteps>>;
  /** New players' first days (from 12 Oct 2026), by device: how many in each bucket (firstVisitKey). */
  firstVisits?: Partial<Record<Device, Record<string, number>>>;
  /** New players by where they came from (from 13 Oct 2026; net/arrival.ts labels). */
  sites?: Record<string, SiteCounts>;
  phones: number;
  computers: number;
  /** Played with a controller (on a phone or a computer). */
  pads: number;
  /** Half the players drove longer than this, ms. */
  medianRacingMs: number;
  /** Players by time driving: none, under a minute, 1–5 minutes, 5–15, over 15. */
  timeBands: number[];
  /**
   * When they played (from 14 Oct 2026), by device: players in each of the day's 24 hours in Rome, and in each
   * hour on their own clock.
   */
  hours?: Partial<Record<Device, number[]>>;
  localHours?: Partial<Record<Device, number[]>>;
  /** Players by their clock's offset from UTC, minutes ("120": Rome in summer). */
  zones?: Record<string, number>;
  /**
   * Where they played (from 14 Oct 2026): players and new players by the country Cloudflare places their
   * connection in (ISO codes), and the cities with the most players (CITIES_KEPT). Never an address, and never
   * inside Discord, whose proxy is one place for everyone.
   */
  countries?: Record<string, [players: number, newPlayers: number]>;
  cities?: City[];
}

/** A city's players on a day: its country, its name, its rough position (a tenth of a degree), how many. */
export type City = [country: string, city: string, lat: number, lon: number, players: number];

/** The cities a day keeps (the most players first): enough for the map, small enough for the stats page to read. */
export const CITIES_KEPT = 40;

/** The hours in a 24-bit mask (bit h: hour h). */
function addHours(into: number[], mask: number): void {
  for (let h = 0; h < 24; h++) if (mask & (1 << h)) into[h]++;
}

/** New players who came from one place: how many, and of them, who pressed race, finished a lap, put one on the board. */
export interface SiteCounts {
  n: number;
  raced: number;
  finished: number;
  saved: number;
}

/** One device's new players, step by step (PlayerTotals.firstSteps). */
export interface FirstSteps {
  /** reached[i]: how many got at least as far as FIRST_STEPS[i]. */
  reached: number[];
  /** Never got the car moving: took the lesson, skipped it, or never answered its offer (or never had one). */
  stuck: { lesson: number; skipped: number; neither: number };
}

/** A day's challenge links (by the day of the lap they point at): how far they reached. */
export interface LinkReach {
  /** Links made, and the players who made them (one player can make a few). */
  made: number;
  senders: number;
  /** Links opened by at least one person, and the people who opened them in all (a link's sender never counts). */
  opened: number;
  opens: number;
  /** The senders whose links reached the most people (today's and yesterday's only: older days keep the counts). */
  top?: { name: string; opens: number }[];
}

/** A day on the stats page: its totals and its players. */
export interface DayStats {
  date: string;
  totals: Partial<Record<TotalKey, number>>;
  players: PlayerTotals | null;
  /** Its challenge links (counted from 7 Oct 2026). */
  links?: LinkReach | null;
  /** Of its new players, those who came back on a later day so far, by device and bucket (firstVisitKey). */
  cameBack?: Partial<Record<Device, Record<string, number>>> | null;
  /** Its new players as a cohort (from 13 Oct 2026). */
  cohort?: CohortDay | null;
  /** Of its new players, those back on a later day so far, by where they came from (from 13 Oct 2026). */
  sitesBack?: Record<string, number> | null;
}

export interface StatsReport {
  /** Today in Rome (the game's day). */
  today: string;
  /** Oldest first, every day of the period (empty days included). */
  days: DayStats[];
  /** The beta's feedback, crashes, and today's and yesterday's boards (to take a lap off). */
  beta?: BetaReport;
  /** The Discord servers the game is in now (null: Discord couldn't be asked; left out: no Discord app). */
  discord?: DiscordNow | null;
}

/** A Discord server the game is in, on the stats page. */
export interface DiscordServer {
  name: string;
  /** Discord's rough count of its members (null: not known). */
  members: number | null;
  /** Players who've played in it. */
  players: number;
  /** When the game was added to it, ms (null: never heard of here). */
  added: number | null;
  /** With the game's bot in it (false: added without, its messages go out as replies). */
  bot: boolean;
}

/** Discord now: asked of Discord as the stats page loads, so a server that removes the game drops off. */
export interface DiscordNow {
  /** The servers the game is in, most players first. */
  servers: DiscordServer[];
  /** Servers that added the game and have removed it since. */
  removed: number;
  /** Servers played in through someone's personal install, without the game added to them. */
  elsewhere: number;
  /** Personal installs (Discord's rough count; null: not known). */
  installs: number | null;
}

/** A server as Discord lists the bot's servers (GET /users/@me/guilds?with_counts=true). */
export interface BotGuild {
  id: string;
  name: string;
  approximate_member_count?: number;
}

/** A server as the game knows it (its guilds row and how many have played there). */
export interface KnownGuild {
  guild_id: string;
  name: string | null;
  created_at: number;
  /** When the game was added to it (from 11 Oct 2026, the welcome message): null for servers only played in. */
  welcomed_at: number | null;
  /** 1: added without the bot (from 16 Oct 2026), so never on the bot's list. */
  app_only?: number | null;
  players: number;
}

/**
 * Discord's list of the bot's servers, with what the game knows of each, and the servers that added the game
 * without the bot (Discord tells nobody when one of those removes it: they stay listed).
 */
export function serversNow(listed: BotGuild[], known: KnownGuild[]): Omit<DiscordNow, 'installs'> {
  const byId = new Map(known.map((k) => [k.guild_id, k]));
  const ids = new Set(listed.map((g) => g.id));
  const gone = known.filter((k) => !ids.has(k.guild_id));
  const appOnly = gone.filter((k) => k.app_only === 1);
  const servers = [
    ...listed.map((g): DiscordServer => {
      const k = byId.get(g.id);
      return {
        name: g.name || k?.name || 'a server',
        members: typeof g.approximate_member_count === 'number' ? g.approximate_member_count : null,
        players: k?.players ?? 0,
        added: k ? (k.welcomed_at ?? k.created_at) : null,
        bot: true,
      };
    }),
    ...appOnly.map((k): DiscordServer => ({ name: k.name || 'a server', members: null, players: k.players, added: k.welcomed_at ?? k.created_at, bot: false })),
  ];
  servers.sort((a, b) => b.players - a.players || (b.members ?? 0) - (a.members ?? 0) || (b.added ?? 0) - (a.added ?? 0));
  const others = gone.filter((k) => k.app_only !== 1);
  return {
    servers,
    removed: others.filter((k) => k.welcomed_at != null).length,
    elsewhere: others.filter((k) => k.welcomed_at == null && k.players > 0).length,
  };
}

/**
 * How this browser's last tally went ('204' when counted, 'failed' when it never
 * reached the server, 'not counted: …' when the game doesn't count this browser),
 * kept by the game for the stats page's "this browser" line.
 */
export const LAST_TALLY_KEY = 'gp.statsLast';
export interface LastTally {
  at: number;
  result: string;
}

/** Where the time-driving bands start, ms (the last is open-ended). */
export const TIME_BANDS = [0, 1, 60_000, 5 * 60_000, 15 * 60_000];

/** The band a player's time driving falls in (0: didn't drive). */
export function timeBand(ms: number): number {
  let b = 0;
  for (let i = 1; i < TIME_BANDS.length; i++) if (ms >= TIME_BANDS[i]) b = i;
  return b;
}

/** A player's row for a day (clean and named: null when not recorded, on rows from before they were). */
export interface PlayerRow {
  is_new: number;
  prev_day: string;
  device: string;
  input: string;
  racing_ms: number;
  laps: number;
  saved: number;
  clean?: number | null;
  named?: number | null;
  via_link?: number | null;
  recruit?: string | null;
  day_two?: number | null;
  reached?: number | null;
  took_lesson?: number | null;
  fv_tries?: number | null;
  fv_far?: number | null;
  fv_stuck?: number | null;
  in_discord?: number | null;
  site?: string | null;
  hours?: number | null;
  local_hours?: number | null;
  utc_offset?: number | null;
  country?: string | null;
  city?: string | null;
  lat?: number | null;
  lon?: number | null;
}

/** A first day's cohort sizes, from its new players' rows (those whose first day was summed up: they raced). */
export function cohortSizes(rows: PlayerRow[]): CohortDay['size'] {
  const size: CohortDay['size'] = {};
  for (const r of rows) {
    if (!r.is_new || r.fv_far == null) continue;
    const src: CohortSource = r.in_discord ? 'd' : r.via_link ? 'l' : 'w';
    const seg = cohortSeg({ far: r.fv_far, src, lesson: r.took_lesson === 1, board: r.saved === 1 });
    const d = (size[r.device as Device] ??= {});
    d[seg] = (d[seg] ?? 0) + 1;
  }
  return size;
}

/** A day's player totals from its players' rows. */
export function totalPlayers(prevDate: string, rows: PlayerRow[]): PlayerTotals {
  const times = rows.map((r) => r.racing_ms).sort((a, b) => a - b);
  const bands = [0, 0, 0, 0, 0];
  for (const t of times) bands[timeBand(t)]++;
  const n = times.length;
  // The clean-lap counts, among the players whose clean laps and name were both recorded.
  const known = rows.filter((r) => r.clean != null && r.named != null);
  const cleanOnes = known.filter((r) => (r.clean ?? 0) > 0);
  const measuredCounts = known.length
    ? { measured: known.length, clean: cleanOnes.length, cleanNotSaved: cleanOnes.filter((r) => !r.saved).length, cleanNoName: cleanOnes.filter((r) => r.named === 0).length }
    : {};
  // Friends' links, among the players whose games recorded them.
  const linked = rows.filter((r) => r.via_link != null);
  const byLinkNew = linked.filter((r) => r.via_link && r.is_new);
  const linkCounts = linked.length
    ? {
        linkMeasured: linked.length,
        linkMeasuredNew: linked.filter((r) => r.is_new).length,
        byLink: linked.filter((r) => r.via_link).length,
        byLinkNew: byLinkNew.length,
        byLinkNewFinished: byLinkNew.filter((r) => r.laps > 0).length,
        byLinkNewSaved: byLinkNew.filter((r) => r.saved).length,
        secondDay: linked.filter((r) => r.day_two).length,
        recruitsNextDay: linked.filter((r) => r.day_two && r.recruit === prevDate).length,
      }
    : {};
  // First steps, among the new players whose games recorded them.
  const stepped = rows.filter((r) => r.is_new && r.reached != null);
  const firstSteps: Partial<Record<Device, FirstSteps>> = {};
  for (const r of stepped) {
    const d = (firstSteps[r.device as Device] ??= { reached: FIRST_STEPS.map(() => 0), stuck: { lesson: 0, skipped: 0, neither: 0 } });
    for (let i = 0; i <= (r.reached as number); i++) d.reached[i]++;
    if ((r.reached as number) < FIRST_STEPS.indexOf('moving')) d.stuck[r.took_lesson == null ? 'neither' : r.took_lesson ? 'lesson' : 'skipped']++;
  }
  // First days, among the new players whose games summed them up.
  const firstVisits: Partial<Record<Device, Record<string, number>>> = {};
  let summed = 0;
  for (const r of rows) {
    if (!r.is_new || r.fv_tries == null || r.fv_far == null || r.fv_stuck == null) continue;
    const k = firstVisitKey({ tries: r.fv_tries, far: r.fv_far, stuck: r.fv_stuck });
    const d = (firstVisits[r.device as Device] ??= {});
    d[k] = (d[k] ?? 0) + 1;
    summed++;
  }
  // Where new players came from, among those whose games said.
  const sites: Record<string, SiteCounts> = {};
  for (const r of rows) {
    if (!r.is_new || !r.site) continue;
    const c = (sites[r.site] ??= { n: 0, raced: 0, finished: 0, saved: 0 });
    c.n++;
    if ((r.reached ?? -1) >= FIRST_STEPS.indexOf('race') || r.fv_tries != null) c.raced++;
    if (r.laps > 0) c.finished++;
    if (r.saved) c.saved++;
  }
  // When and where, among the players whose rows have them.
  const hours: Partial<Record<Device, number[]>> = {};
  const localHours: Partial<Record<Device, number[]>> = {};
  const zones: Record<string, number> = {};
  const countries: Record<string, [number, number]> = {};
  const cities = new Map<string, City>();
  for (const r of rows) {
    const dev = r.device as Device;
    if (r.hours) addHours((hours[dev] ??= Array<number>(24).fill(0)), r.hours);
    if (r.local_hours) addHours((localHours[dev] ??= Array<number>(24).fill(0)), r.local_hours);
    if (r.utc_offset != null) zones[r.utc_offset] = (zones[r.utc_offset] ?? 0) + 1;
    if (!r.country) continue;
    const c = (countries[r.country] ??= [0, 0]);
    c[0]++;
    if (r.is_new) c[1]++;
    if (!r.city || r.lat == null || r.lon == null) continue;
    const k = `${r.country}|${r.city}`;
    const city = cities.get(k) ?? [r.country, r.city, r.lat, r.lon, 0];
    city[4]++;
    cities.set(k, city);
  }
  const when = Object.keys(hours).length ? { hours, ...(Object.keys(localHours).length ? { localHours } : {}) } : {};
  const where = Object.keys(countries).length ? { countries, cities: [...cities.values()].sort((a, b) => b[4] - a[4]).slice(0, CITIES_KEPT) } : {};
  return {
    players: n,
    newPlayers: rows.filter((r) => r.is_new).length,
    fromYesterday: rows.filter((r) => r.prev_day === prevDate).length,
    finished: rows.filter((r) => r.laps > 0).length,
    saved: rows.filter((r) => r.saved).length,
    ...measuredCounts,
    ...linkCounts,
    ...(stepped.length ? { firstSteps } : {}),
    ...(summed ? { firstVisits } : {}),
    ...(Object.keys(sites).length ? { sites } : {}),
    phones: rows.filter((r) => r.device === 'phone').length,
    computers: rows.filter((r) => r.device === 'computer').length,
    pads: rows.filter((r) => r.input === 'pad').length,
    medianRacingMs: n === 0 ? 0 : n % 2 ? times[(n - 1) / 2] : Math.round((times[n / 2 - 1] + times[n / 2]) / 2),
    timeBands: bands,
    ...when,
    ...(Object.keys(zones).length ? { zones } : {}),
    ...where,
  };
}
